/**
 * Welcome First Login — shown once after a magic-link invite is accepted.
 * Prompts the user to confirm their display name and writes it to
 * users/{uid}/profile/{base}.displayName via the relaxed owner-write rule.
 *
 * The "Welcome pending" flag is sessionStorage['deepatomic_pending_welcome']
 * — set by InviteLanding after a successful acceptInvite, cleared here.
 */
import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { Loader2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { BrandLogo } from './auth/BrandLogo';
import { toast } from 'sonner';

const PENDING_WELCOME_KEY = 'deepatomic_pending_welcome';
const PROFILE_DOC_ID = 'base';

export function isWelcomePending(): boolean {
  try { return sessionStorage.getItem(PENDING_WELCOME_KEY) === '1'; } catch { return false; }
}

export function clearWelcomePending(): void {
  try { sessionStorage.removeItem(PENDING_WELCOME_KEY); } catch { /* ignore */ }
}

interface Props {
  onComplete: () => void;
}

export function WelcomeFirstLogin({ onComplete }: Props) {
  const user = auth.currentUser;
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [saving, setSaving] = useState(false);

  // Prefill from the profile seed — `acceptInvite` writes
  // users/{uid}/profile/base.displayName (the invite-carried name when present,
  // else the email prefix), so the field shows the name set at invite time
  // rather than starting empty. Best-effort; only fills an empty field.
  const uid = user?.uid;
  React.useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, `users/${uid}/profile`, PROFILE_DOC_ID));
        const seed = (snap.data()?.displayName as string | undefined)?.trim();
        if (!cancelled && seed) setDisplayName(prev => (prev.trim() ? prev : seed));
      } catch { /* best-effort prefill */ }
    })();
    return () => { cancelled = true; };
  }, [uid]);

  if (!user) {
    // Defensive — AuthGate gates Welcome on a signed-in user, so this is unreachable.
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = displayName.trim();
    if (!name) { toast.error('Please enter a display name'); return; }
    if (saving) return;
    setSaving(true);
    try {
      // Owner-write per the firestore.rules profile field whitelist.
      const { doc, setDoc } = await import('firebase/firestore');
      await setDoc(
        doc(db, `users/${user.uid}/profile`, PROFILE_DOC_ID),
        { displayName: name },
        { merge: true },
      );
      // Also update the Firebase Auth profile — the top-right user menu reads
      // auth.currentUser.displayName, so the Firestore write alone never
      // reached it (the name appeared "not saved"). Best-effort; Firestore
      // profile/base remains the source of truth.
      try {
        const { updateProfile } = await import('firebase/auth');
        await updateProfile(user, { displayName: name });
      } catch { /* Auth profile is best-effort */ }
      clearWelcomePending();
      toast.success(`Welcome, ${name}`);
      onComplete();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not save your display name';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    clearWelcomePending();
    onComplete();
  };

  return (
    <div className="h-screen w-screen bg-background flex items-center justify-center font-mono">
      <div className="max-w-md w-full mx-4 space-y-6">
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center">
            <BrandLogo height={44} />
          </div>
          <div>
            <h1 className="text-lg font-bold uppercase tracking-[0.3em] text-text-primary">Welcome</h1>
            <p className="text-[9px] text-text-muted uppercase tracking-widest mt-1">{user.email}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="border-2 border-border bg-background p-6 space-y-4">
          <div className="space-y-1">
            <label className="text-[9px] font-bold uppercase tracking-widest text-text-secondary">
              Display name
            </label>
            <input
              type="text" value={displayName} onChange={e => setDisplayName(e.target.value)}
              placeholder="Jane Doe" autoFocus maxLength={80}
              className="w-full h-10 px-3 text-sm border-2 border-border bg-background text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary rounded-md"
            />
            <div className="text-[8px] text-text-muted uppercase tracking-widest pt-1">
              Visible to teammates on reports and in the activity log.
            </div>
          </div>

          <Button
            type="submit" disabled={saving || !displayName.trim()}
            className="w-full h-10 rounded-md uppercase tracking-widest text-[10px] font-bold"
          >
            {saving
              ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Saving…</>
              : <>Continue <ArrowRight className="w-3.5 h-3.5 ml-2" /></>}
          </Button>
          <button
            type="button" onClick={handleSkip} disabled={saving}
            className="w-full text-[9px] text-text-muted hover:text-text-secondary uppercase tracking-widest"
          >
            Skip for now
          </button>
        </form>
      </div>
    </div>
  );
}
